import { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, History, Settings, Download, Loader2, Save, ChevronDown, ChevronUp, Search, Plus, Trash2, PenTool, Coins, ExternalLink, Upload } from 'lucide-react';
import { idsApi } from '@/services/idsApi';
import IdsOfRecordPanel from './IdsOfRecordPanel';
import IdsPriorArtDialog, { type ConfirmedArt } from './IdsPriorArtDialog';
import { useNotificationStore } from '@/stores';
import { NAME_PREFERENCE_KINDS } from '@/types/ids';
import type { IDSFormData, IDSSubmission, IDSSettings, SortField, SortDirection, IDSSignature, NamePreference, ExtractedArt } from '@/types/ids';
import { validateCompleteIDSForm } from '@/utils/idsValidation';
import { cleanDocumentNumber } from '@/utils/documentNumbers';

/**
 * Whether the "art already cited" panel is shown.
 *
 * Off for now at the user’s request. Everything behind it is kept and still works;
 * this only decides whether the panel is on the page.
 */
const SHOW_ART_ALREADY_CITED = false;

export function IDSPage() {
  const { addNotification } = useNotificationStore();
  const [activeTab, setActiveTab] = useState('create');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [history, setHistory] = useState<IDSSubmission[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showResults, setShowResults] = useState(false);
  const [generatedIDS, setGeneratedIDS] = useState<IDSSubmission | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingPdfs, setDownloadingPdfs] = useState<Record<string, boolean>>({});
  const [downloadPdfStatus, setDownloadPdfStatus] = useState<Record<string, string>>({});
  // Reading uploaded prior art for its document numbers. The dialog opens as soon as the
  // upload lands and fills in when the job finishes, so the filer watches the reading
  // rather than a button that spins with nothing to show.
  const [artDialogOpen, setArtDialogOpen] = useState(false);
  // Which read is the current one. Closing the dialog bumps it, and the poll below stops
  // the moment its own number is no longer the current one - otherwise Cancel closed the
  // dialog and left the poll running, the button stuck on "Reading…", and a result
  // arriving later re-opened nothing while still clearing the spinner.
  const artRunRef = useRef(0);
  const [artReading, setArtReading] = useState(false);
  const [artExtracted, setArtExtracted] = useState<ExtractedArt | null>(null);
  const [artError, setArtError] = useState('');
  const [lookingUpDocket, setLookingUpDocket] = useState(false);
  const [docketMatches, setDocketMatches] = useState<any[]>([]);
  const [formData, setFormData] = useState<IDSFormData>({
    appNo: '',
    docketNo: '',
    idsName: '',
    filingDate: '',
    firstInventor: '',
    artUnit: '',
    examinerName: '',
    referencesText: '',
    foreignReferencesText: '',
    nplText: '',
  });
  const [settings, setSettings] = useState<IDSSettings>({
    usPatentsSortField: 'input_order',
    usPatentsSortDirection: 'asc',
    usPublicationsSortField: 'input_order',
    usPublicationsSortDirection: 'asc',
    foreignPatentsSortField: 'input_order',
    foreignPatentsSortDirection: 'asc',
    docketNumberPreference: 'retrieved',
    namePreferences: {
      usPatents: 'inventor',
      usPublications: 'inventor',
      foreignPatents: 'inventor',
    },
    signatures: [],
  });
  const [newSignature, setNewSignature] = useState({ name: '', registrationNumber: '', label: '' });

  // Fill the form from USPTO using the attorney docket number - the same lookup the
  // docket tool runs when adding a matter. A docket number is a prefix and is not unique
  // at USPTO, so several hits are offered rather than one chosen.
  const applyDocketMatch = (match: any) => {
    setFormData(prev => ({
      ...prev,
      appNo: match.appNumber || match.appNo || prev.appNo,
      docketNo: match.docketNumber || prev.docketNo,
      filingDate: match.filingDate || prev.filingDate,
      firstInventor: match.firstInventor || match.applicant || prev.firstInventor,
      artUnit: match.artUnit || prev.artUnit,
      examinerName: match.examiner || match.examinerName || prev.examinerName,
    }));
    setDocketMatches([]);
    addNotification({
      type: 'success',
      title: 'Application found',
      message: `Matched application ${match.appNumber || match.appNo}${match.title ? ` — ${match.title}` : ''}.`,
    });
  };

  const handleDocketLookup = async () => {
    const docket = formData.docketNo.trim();
    if (!docket) {
      addNotification({
        type: 'error',
        title: 'Docket number required',
        message: 'Enter the attorney docket number to look up.',
      });
      return;
    }

    setLookingUpDocket(true);
    setDocketMatches([]);
    try {
      const found = await idsApi.lookupByDocket(docket);
      const results = found?.matches || [];
      if (results.length === 1) {
        applyDocketMatch(results[0]);
      } else if (results.length > 1) {
        setDocketMatches(results);
      } else {
        addNotification({
          type: 'error',
          title: 'No application found',
          message: found?.error || `USPTO has no application with docket number ${docket}.`,
        });
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Lookup failed',
        message: error?.message || 'Could not reach USPTO for that search.',
      });
    } finally {
      setLookingUpDocket(false);
    }
  };

  const handleInputChange = (field: keyof IDSFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Read uploaded prior art for its document numbers, then show it to be confirmed.
   *
   * Upload, queue, poll. Queued rather than answered inline because a set of scanned
   * patents is minutes of OCR, which no request will wait for — the same reason the IDS
   * itself is generated by the worker.
   */
  const handlePriorArtUpload = async (files: FileList | null) => {
    const chosen = Array.from(files || []);
    if (chosen.length === 0) return;

    const run = artRunRef.current + 1;
    artRunRef.current = run;
    const abandoned = () => artRunRef.current !== run;

    setArtExtracted(null);
    setArtError('');
    setArtReading(true);
    setArtDialogOpen(true);

    try {
      const { keys } = await idsApi.uploadPriorArt(chosen);
      if (abandoned()) return;
      const { jobId } = await idsApi.startPriorArtExtract(keys, formData.appNo || undefined);
      if (abandoned()) return;

      // Polled rather than awaited: the job runs in the worker's long lane and the page
      // stays usable while it does. Capped so a job that dies silently stops the poll
      // instead of leaving the dialog spinning for ever.
      const deadline = Date.now() + 10 * 60 * 1000;
      for (;;) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        // Abandoned: the filer closed the dialog. The worker finishes the job either way
        // - it is already queued - but nothing here touches the page any more.
        if (abandoned()) return;
        const { status, error, extracted } = await idsApi.getPriorArtResult(jobId);
        if (abandoned()) return;
        if (status === 'complete') {
          setArtExtracted(extracted);
          if (!extracted || extracted.documents.length === 0) {
            setArtError('Nothing could be read from those documents.');
          }
          break;
        }
        if (status === 'failed' || status === 'error') {
          setArtError(error || 'Those documents could not be read.');
          break;
        }
        if (Date.now() > deadline) {
          setArtError('That took too long. Try fewer documents at once.');
          break;
        }
      }
    } catch (err: any) {
      if (abandoned()) return;
      // The server's own message where it gave one: "Only PDF documents can be read"
      // tells the filer what to do, where "upload failed" does not.
      const message =
        err?.response?.data?.error || err?.message || 'Those documents could not be read.';
      setArtError(message);
    } finally {
      // Only the current read may clear the spinner. A late return from an abandoned one
      // would otherwise switch off the spinner belonging to a read started after it.
      if (!abandoned()) setArtReading(false);
    }
  };

  /** Closing the dialog abandons the read in progress. */
  const handleArtDialogChange = (open: boolean) => {
    setArtDialogOpen(open);
    if (!open) {
      artRunRef.current += 1;
      setArtReading(false);
    }
  };

  /**
   * Put the confirmed citations into the form.
   *
   * Appended, never overwritten: a filer who has typed ten numbers and then uploads the
   * documents for five more means to end up with fifteen. Duplicates are left to the
   * de-duplication the form already runs on generate, which is the one place that knows
   * how each kind of number compares.
   */
  const handlePriorArtConfirm = (confirmed: ConfirmedArt) => {
    const append = (current: string, additions: string[]) => {
      if (additions.length === 0) return current;
      const existing = current.trim();
      return existing ? `${existing}\n${additions.join('\n')}` : additions.join('\n');
    };

    setFormData(prev => ({
      ...prev,
      referencesText: append(prev.referencesText, confirmed.referencesText),
      foreignReferencesText: append(
        prev.foreignReferencesText,
        confirmed.foreignReferencesText
      ),
      nplText: append(prev.nplText, confirmed.nplText),
    }));

    const total =
      confirmed.referencesText.length +
      confirmed.foreignReferencesText.length +
      confirmed.nplText.length;
    addNotification({
      title: 'References added',
      message: `${total} reference${total === 1 ? '' : 's'} added to the form. Check them before generating.`,
      type: 'success',
    });
  };

  const handleSubmit = async () => {
    // Validate form
    const validation = validateCompleteIDSForm(formData);
    if (!validation.isValid) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: validation.errors.join(', '),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Helper function to remove duplicates from a text field
      const removeDuplicates = (text: string, isUSPatents: boolean = false, isForeign = false): string => {
        if (!text.trim()) return text;

        const lines = text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        // Dedupe on the cleaned form and *send* the cleaned form. This used to key on the
        // cleaned number but return the original line, so "US 7,123,456 B2" went to the
        // worker as typed and was cleaned there - partially, and only on some code paths.
        // Cleaning once here is the rule for every number that leaves the browser. NPL
        // entries are free text and pass through untouched.
        const seen = new Map<string, string>();

        lines.forEach(line => {
          let value = line;
          if (isUSPatents) value = cleanDocumentNumber(line, 'us_reference');
          else if (isForeign) value = cleanDocumentNumber(line, 'foreign_patent');
          const key = isUSPatents || isForeign ? value : line.toLowerCase();

          // Keep first occurrence
          if (!seen.has(key)) {
            seen.set(key, value);
          }
        });

        return Array.from(seen.values()).join('\n');
      };

      // Remove duplicates within each section
      const deduplicatedReferences = removeDuplicates(formData.referencesText, true); // US patents/pubs - cleaned
      const deduplicatedForeignRefs = removeDuplicates(formData.foreignReferencesText, false, true); // Foreign - separators removed, codes kept
      const deduplicatedNPL = removeDuplicates(formData.nplText, false); // NPL - free text, case-insensitive dedupe only

      // Get selected signature
      const signatureId = formData.signatureId || settings.defaultSignatureId;
      const selectedSignature = signatureId
        ? settings.signatures?.find(sig => sig.id === signatureId)
        : undefined;

      const jobId = await idsApi.submitAsync({
        appNo: cleanDocumentNumber(formData.appNo, 'application'),
        dktNo: formData.docketNo, // Backend expects 'dktNo'
        idsName: formData.idsName,
        entries: deduplicatedReferences,
        foreignEntries: deduplicatedForeignRefs,
        nplEntries: deduplicatedNPL,
        getForeign: 'false',
        justCheck: 'false',
        // Only the fields actually filled in; a blank must not override the record.
        applicationInfo: {
          ...(formData.filingDate?.trim() ? { filingDate: formData.filingDate.trim() } : {}),
          ...(formData.firstInventor?.trim() ? { firstInventor: formData.firstInventor.trim() } : {}),
          ...(formData.artUnit?.trim() ? { artUnit: formData.artUnit.trim() } : {}),
          ...(formData.examinerName?.trim() ? { examinerName: formData.examinerName.trim() } : {}),
        },
        signature: selectedSignature ? {
          name: selectedSignature.name,
          registrationNumber: selectedSignature.registrationNumber,
        } : undefined,
      });

      addNotification({
        type: 'info',
        title: 'IDS Submission Started',
        message: 'Your IDS form is being generated. This may take a few moments.',
      });

      // Wait for completion
      const result = await idsApi.waitForJobCompletion(jobId, (status) => {
        console.log('IDS generation progress:', status);
      });

      if (result.status === 'complete') {
        addNotification({
          type: 'success',
          title: 'IDS Generated',
          message: 'Your IDS form has been generated successfully.',
        });

        // Trigger credit update (credits may have been deducted for free users)
        window.dispatchEvent(new Event('credits-updated'));

        // The job says which file it just wrote. Identify the new submission by that
        // key rather than by searching history for a name.
        //
        // Searching by name returned the WRONG IDS in two separate ways, both of which
        // show up as "it keeps giving me an old one":
        //
        //   - `.find()` returns the FIRST match, and history came back oldest-first, so
        //     re-using an IDS name for a case handed back the first one ever generated
        //     under that name.
        //   - The history row is written by the worker and read back immediately here.
        //     A DynamoDB query is eventually consistent, so the new row is sometimes not
        //     in that read at all — and the `|| historyData[0]` fallback then picked
        //     whatever sat at index 0.
        //
        // The job's own s3_key is subject to neither, because it does not depend on the
        // history read succeeding.
        const generatedKey = result.s3_key || result.fileKey || '';

        const historyData = await idsApi.getHistory();
        const byKey = generatedKey
          ? historyData.find((item: IDSSubmission) => item.fileKey === generatedKey)
          : undefined;

        // Newest first, so "most recent match" means what it says whichever way the
        // server happens to order the list.
        const newestFirst = [...historyData].sort(
          (a: IDSSubmission, b: IDSSubmission) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        const latestIDS =
          byKey ||
          newestFirst.find((item: IDSSubmission) =>
            item.idsName === formData.idsName && item.appNo === formData.appNo
          ) ||
          newestFirst[0];

        if (latestIDS || generatedKey) {
          if (latestIDS) {
            setGeneratedIDS(latestIDS);
            setShowResults(true);
          }

          // Download the file this run produced. Falling back to the history row's key
          // would re-download an older PDF whenever the row is stale or missing.
          const downloadKey = generatedKey || latestIDS?.fileKey;
          if (downloadKey) {
            await handleDownload(downloadKey, `${formData.idsName || latestIDS?.idsName}.pdf`);
          }
        }
      }
    } catch (error: any) {
      console.error('IDS submission error:', error);
      addNotification({
        type: 'error',
        title: 'Submission Failed',
        message: error.message || 'Failed to generate IDS form',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadHistory = async () => {
    try {
      const historyData = await idsApi.getHistory();
      setHistory(historyData);
    } catch (error) {
      console.error('Failed to load IDS history:', error);
    }
  };

  const handleDownload = async (fileKey: string, filename: string) => {
    try {
      const blob = await idsApi.downloadFile(fileKey);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: error.message || 'Failed to download file',
      });
    }
  };

  const handleDownloadReferencePdfs = async (item: IDSSubmission) => {
    const key = item.idsId;
    const usPatents = item.usPatents || [];
    const usApplications = item.usApplications || [];
    const foreignPatents = item.foreignPatents || [];
    const totalRefs = usPatents.length + usApplications.length + foreignPatents.length;

    if (totalRefs === 0) {
      addNotification({
        type: 'error',
        title: 'No References',
        message: 'This IDS has no patent references to download.',
      });
      return;
    }

    setDownloadingPdfs(prev => ({ ...prev, [key]: true }));
    setDownloadPdfStatus(prev => ({ ...prev, [key]: `Queuing ${totalRefs} PDFs...` }));

    try {
      const jobId = await idsApi.downloadReferencePdfs({
        idsName: item.idsName,
        usPatents,
        usApplications,
        foreignPatents,
      });

      setDownloadPdfStatus(prev => ({ ...prev, [key]: 'Downloading PDFs...' }));

      const result = await idsApi.waitForJobCompletion(jobId, (status) => {
        if (status.status === 'Processing') {
          setDownloadPdfStatus(prev => ({ ...prev, [key]: 'Downloading PDFs...' }));
        }
      });

      if (result.status === 'complete' && (result.idsKey || result.result)) {
        const fileKey = result.idsKey || result.result;
        const isZip = fileKey.endsWith('.zip');
        const ext = isZip ? 'zip' : 'pdf';
        const filename = `${item.idsName}_references.${ext}`;

        setDownloadPdfStatus(prev => ({ ...prev, [key]: 'Preparing download...' }));

        const blob = await idsApi.downloadFile(fileKey);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        addNotification({
          type: 'success',
          title: 'Reference PDFs Downloaded',
          message: `Downloaded reference PDFs for "${item.idsName}".`,
        });
      }
    } catch (error: any) {
      console.error('Reference PDF download error:', error);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: error.message || 'Failed to download reference PDFs',
      });
    } finally {
      setDownloadingPdfs(prev => ({ ...prev, [key]: false }));
      setDownloadPdfStatus(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const loadSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const savedSettings = await idsApi.getSettings();
      if (savedSettings && Object.keys(savedSettings).length > 0) {
        setSettings(prev => ({ ...prev, ...savedSettings }));
      }
    } catch (error) {
      console.error('Failed to load IDS settings:', error);
      addNotification({
        type: 'error',
        title: 'Settings Load Failed',
        message: 'Failed to load your IDS settings',
      });
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await idsApi.updateSettings(settings);
      addNotification({
        type: 'success',
        title: 'Settings Saved',
        message: 'Your IDS generation preferences have been saved.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to save settings',
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSettingChange = <K extends keyof IDSSettings>(field: K, value: IDSSettings[K]) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const toggleExpanded = (idsId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idsId)) {
        newSet.delete(idsId);
      } else {
        newSet.add(idsId);
      }
      return newSet;
    });
  };

  const handleGenerateNew = () => {
    setShowResults(false);
    setGeneratedIDS(null);
    setFormData({
      appNo: '',
      docketNo: '',
      idsName: '',
      referencesText: '',
      foreignReferencesText: '',
      nplText: '',
    });
  };

  // Signature management
  const handleAddSignature = async () => {
    if (!newSignature.name.trim() || !newSignature.registrationNumber.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Signature',
        message: 'Please enter name, signature, and registration number.',
      });
      return;
    }

    const signature: IDSSignature = {
      id: `sig_${Date.now()}`,
      name: newSignature.name.trim(),
      registrationNumber: newSignature.registrationNumber.trim(),
      label: newSignature.label.trim() || undefined,
    };

    // Create updated settings with new signature
    const updatedSettings = {
      ...settings,
      signatures: [...(settings.signatures || []), signature],
    };

    // Update state
    setSettings(updatedSettings);

    // Save to backend immediately
    try {
      setIsSavingSettings(true);
      await idsApi.updateSettings(updatedSettings);

      setNewSignature({ name: '', registrationNumber: '', label: '' });

      addNotification({
        type: 'success',
        title: 'Signature Added',
        message: 'Signature has been added and saved.',
      });
    } catch (error: any) {
      // Revert state on error
      setSettings(settings);
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to save signature',
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleRemoveSignature = async (signatureId: string) => {
    // Create updated settings with signature removed
    const updatedSettings = {
      ...settings,
      signatures: (settings.signatures || []).filter(sig => sig.id !== signatureId),
      defaultSignatureId: settings.defaultSignatureId === signatureId ? undefined : settings.defaultSignatureId,
    };

    // Update state
    setSettings(updatedSettings);

    // Save to backend immediately
    try {
      setIsSavingSettings(true);
      await idsApi.updateSettings(updatedSettings);

      addNotification({
        type: 'success',
        title: 'Signature Removed',
        message: 'Signature has been removed and settings saved.',
      });
    } catch (error: any) {
      // Revert state on error
      setSettings(settings);
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to remove signature',
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSetDefaultSignature = async (signatureId: string) => {
    // Create updated settings with new default
    const updatedSettings = {
      ...settings,
      defaultSignatureId: signatureId,
    };

    // Update state
    setSettings(updatedSettings);

    // Save to backend immediately
    try {
      setIsSavingSettings(true);
      await idsApi.updateSettings(updatedSettings);

      addNotification({
        type: 'success',
        title: 'Default Signature Set',
        message: 'Default signature has been updated and saved.',
      });
    } catch (error: any) {
      // Revert state on error
      setSettings(settings);
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to set default signature',
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Filter and sort history
  const getFilteredAndSortedHistory = () => {
    // First sort by timestamp descending (most recent first)
    const sorted = [...history].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA; // Descending order
    });

    // If no search query, return sorted list
    if (!searchQuery.trim()) {
      return sorted;
    }

    // Filter by search query (case-insensitive wildcard search)
    const query = searchQuery.toLowerCase();
    return sorted.filter((item) => {
      // Search in basic fields
      if (item.idsName?.toLowerCase().includes(query)) return true;
      if (item.appNo?.toLowerCase().includes(query)) return true;
      if (item.docketNo?.toLowerCase().includes(query)) return true;

      // Search in application info
      if (item.applicationInfo) {
        const appInfo = item.applicationInfo;
        if (appInfo.filingDate?.toLowerCase().includes(query)) return true;
        if (appInfo.firstInventor?.toLowerCase().includes(query)) return true;
        if (appInfo.artUnit?.toLowerCase().includes(query)) return true;
        if (appInfo.examinerName?.toLowerCase().includes(query)) return true;
        if (appInfo.attorneyDocket?.toLowerCase().includes(query)) return true;
      }

      // Search in US Patents
      if (item.usPatents?.some((p: any) =>
        p.patent_number?.toLowerCase().includes(query) ||
        p.patentee?.toLowerCase().includes(query) ||
        p.issue_date?.toLowerCase().includes(query)
      )) return true;

      // Search in US Applications
      if (item.usApplications?.some((p: any) =>
        p.publication_number?.toLowerCase().includes(query) ||
        p.applicant?.toLowerCase().includes(query) ||
        p.publication_date?.toLowerCase().includes(query)
      )) return true;

      // Search in Foreign Patents
      if (item.foreignPatents?.some((p: any) =>
        p.country?.toLowerCase().includes(query) ||
        p.docNumber?.toLowerCase().includes(query) ||
        p.patentee?.toLowerCase().includes(query) ||
        p.kind?.toLowerCase().includes(query) ||
        p.date?.toLowerCase().includes(query)
      )) return true;

      // Search in NPL
      if (item.nonPatentLiterature?.some((n: any) =>
        n.citation?.toLowerCase().includes(query)
      )) return true;

      return false;
    });
  };

  const filteredHistory = getFilteredAndSortedHistory();

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto">
        <div className="mb-3">
          <h1 className="text-3xl font-bold text-gray-900">Information Disclosure Statement (IDS)</h1>
          <p className="text-gray-600 mt-2">Generate IDS forms for USPTO submissions</p>

          <div className="mt-3 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3 inline-flex items-center gap-4">
            <Coins className="w-5 h-5 text-blue-600" />
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-700">Free:</span>
                <span className="ml-1 text-gray-600">10 credits/form</span>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <div>
                <span className="font-semibold text-gray-700">Pro/Premium:</span>
                <span className="ml-1 text-green-700 font-medium">Unlimited (0 credits)</span>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="create" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Create IDS
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2" onClick={loadHistory}>
              <History className="w-4 h-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2" onClick={loadSettings}>
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="mt-2">
            <Card>
              <CardHeader className="pb-3 pt-3">
                <CardTitle className="text-lg">Create New IDS Form</CardTitle>
                <CardDescription className="text-sm">
                  Enter your application information and references to generate an IDS form
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Loading State */}
                {isSubmitting && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-3" />
                    <h3 className="text-base font-semibold mb-1">Generating IDS Form</h3>
                    <p className="text-sm text-gray-600">Fetching patent data and creating your IDS form...</p>
                  </div>
                )}

                {/* Results View */}
                {!isSubmitting && showResults && generatedIDS && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 text-green-800 mb-2">
                        <FileText className="w-5 h-5" />
                        <h3 className="font-semibold">IDS Form Generated Successfully!</h3>
                      </div>
                      <p className="text-sm text-green-700">
                        Your IDS form "{generatedIDS.idsName}" has been created and is ready to download.
                      </p>
                    </div>

                    {/* Application Information */}
                    {generatedIDS.applicationInfo && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold border-b pb-2">Application Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div><span className="font-medium text-gray-600">Application Number:</span> {generatedIDS.appNo}</div>
                          {generatedIDS.docketNo && (
                            <div><span className="font-medium text-gray-600">Docket Number:</span> {generatedIDS.docketNo}</div>
                          )}
                          {generatedIDS.applicationInfo.filingDate && (
                            <div><span className="font-medium text-gray-600">Filing Date:</span> {generatedIDS.applicationInfo.filingDate}</div>
                          )}
                          {generatedIDS.applicationInfo.firstInventor && (
                            <div><span className="font-medium text-gray-600">First Inventor:</span> {generatedIDS.applicationInfo.firstInventor}</div>
                          )}
                          {generatedIDS.applicationInfo.artUnit && (
                            <div><span className="font-medium text-gray-600">Art Unit:</span> {generatedIDS.applicationInfo.artUnit}</div>
                          )}
                          {generatedIDS.applicationInfo.examinerName && (
                            <div><span className="font-medium text-gray-600">Examiner:</span> {generatedIDS.applicationInfo.examinerName}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* US Patents */}
                    {generatedIDS.usPatents && generatedIDS.usPatents.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold border-b pb-2">US Patents ({generatedIDS.usPatents.length})</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {generatedIDS.usPatents.map((patent: any, idx: number) => (
                            <div key={idx} className="pl-3 border-l-2 border-blue-300 py-1">
                              <span className="font-medium">{patent.patent_number}</span>
                              {patent.patentee && <span className="text-gray-600"> - {patent.patentee}</span>}
                              {patent.issue_date && <span className="text-gray-500 text-sm"> ({patent.issue_date})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* US Applications */}
                    {generatedIDS.usApplications && generatedIDS.usApplications.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold border-b pb-2">US Publications ({generatedIDS.usApplications.length})</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {generatedIDS.usApplications.map((pub: any, idx: number) => (
                            <div key={idx} className="pl-3 border-l-2 border-green-300 py-1">
                              <span className="font-medium">{pub.publication_number}</span>
                              {pub.applicant && <span className="text-gray-600"> - {pub.applicant}</span>}
                              {pub.publication_date && <span className="text-gray-500 text-sm"> ({pub.publication_date})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Foreign Patents */}
                    {generatedIDS.foreignPatents && generatedIDS.foreignPatents.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold border-b pb-2">Foreign Patents ({generatedIDS.foreignPatents.length})</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {generatedIDS.foreignPatents.map((foreign: any, idx: number) => (
                            <div key={idx} className="pl-3 border-l-2 border-purple-300 py-1">
                              <span className="font-medium">{foreign.country}{foreign.docNumber}{foreign.kind}</span>
                              {foreign.patentee && <span className="text-gray-600"> - {foreign.patentee}</span>}
                              {foreign.date && <span className="text-gray-500 text-sm"> ({foreign.date})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* NPL */}
                    {generatedIDS.nonPatentLiterature && generatedIDS.nonPatentLiterature.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold border-b pb-2">Non-Patent Literature ({generatedIDS.nonPatentLiterature.length})</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {generatedIDS.nonPatentLiterature.map((npl: any, idx: number) => (
                            <div key={idx} className="pl-3 border-l-2 border-orange-300 py-1 text-sm">
                              {npl.citation}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={handleGenerateNew}
                      >
                        Generate New IDS
                      </Button>
                      {generatedIDS.fileKey && (
                        <Button
                          onClick={() => handleDownload(generatedIDS.fileKey!, `${generatedIDS.idsName}.pdf`)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      )}
                      {((generatedIDS.usPatents && generatedIDS.usPatents.length > 0) ||
                        (generatedIDS.usApplications && generatedIDS.usApplications.length > 0) ||
                        (generatedIDS.foreignPatents && generatedIDS.foreignPatents.length > 0)) && (
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadReferencePdfs(generatedIDS)}
                          disabled={downloadingPdfs[generatedIDS.idsId]}
                        >
                          {downloadingPdfs[generatedIDS.idsId] ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{downloadPdfStatus[generatedIDS.idsId] || 'Downloading...'}</>
                          ) : (
                            <><Download className="w-4 h-4 mr-2" />Reference PDFs</>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Form View */}
                {!isSubmitting && !showResults && (
                  <>
                {/* Application Info */}
                <div className="space-y-2">
                  <h3 className="text-base font-semibold">Application Information</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="appNo" className="text-sm">Application Number</Label>
                      <Input
                        id="appNo"
                        placeholder="e.g., 16/123,456"
                        value={formData.appNo}
                        onChange={(e) => handleInputChange('appNo', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="docketNo" className="text-sm">Docket Number (Optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="docketNo"
                          placeholder="e.g., ABC-001"
                          value={formData.docketNo}
                          onChange={(e) => handleInputChange('docketNo', e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleDocketLookup(); } }}
                          className="h-9"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="h-9 shrink-0"
                          onClick={handleDocketLookup}
                          disabled={lookingUpDocket || !formData.docketNo.trim()}
                          title="Find the application at USPTO by docket number"
                        >
                          {lookingUpDocket
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Search className="w-4 h-4" />}
                          <span className="ml-1">Look up</span>
                        </Button>
                      </div>
                    </div>
                  </div>

                  {docketMatches.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                      <div className="text-sm font-medium text-amber-900">
                        {docketMatches.length} applications carry that docket number — choose one
                      </div>
                      <div className="space-y-1">
                        {docketMatches.map((match: any) => (
                          <button
                            key={match.appNumber}
                            type="button"
                            onClick={() => applyDocketMatch(match)}
                            className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-left text-sm hover:bg-amber-100"
                          >
                            <span className="font-medium">{match.docketNumber || '—'}</span>
                            <span className="text-gray-600"> · {match.appNumber}</span>
                            {match.filingDate && <span className="text-gray-500"> · filed {match.filingDate}</span>}
                            {match.title && <div className="text-xs text-gray-600 truncate">{match.title}</div>}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="text-xs text-amber-800 underline"
                        onClick={() => setDocketMatches([])}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="idsName" className="text-sm">IDS Name *</Label>
                    <Input
                      id="idsName"
                      placeholder="e.g., IDS_Initial_Filing"
                      value={formData.idsName}
                      onChange={(e) => handleInputChange('idsName', e.target.value)}
                      required
                      className="h-9"
                    />
                  </div>

                  <details className="rounded-md border border-gray-200 bg-gray-50/60">
                    <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-700">
                      Filing details (optional)
                    </summary>
                    <div className="space-y-3 px-3 pb-3">
                      <p className="text-xs text-gray-500">
                        Filled in from USPTO when the application is on record there. A newly
                        filed application usually is not, so anything you enter here is used
                        instead &mdash; and always takes precedence over the retrieved record.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="filingDate" className="text-sm">Filing Date</Label>
                          <Input
                            id="filingDate"
                            type="date"
                            value={formData.filingDate || ''}
                            onChange={(e) => handleInputChange('filingDate', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="firstInventor" className="text-sm">First Named Inventor</Label>
                          <Input
                            id="firstInventor"
                            placeholder="e.g., Jane Q. Public"
                            value={formData.firstInventor || ''}
                            onChange={(e) => handleInputChange('firstInventor', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="artUnit" className="text-sm">Art Unit</Label>
                          <Input
                            id="artUnit"
                            placeholder="e.g., 3721"
                            value={formData.artUnit || ''}
                            onChange={(e) => handleInputChange('artUnit', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="examinerName" className="text-sm">Examiner Name</Label>
                          <Input
                            id="examinerName"
                            placeholder="e.g., A. Examiner"
                            value={formData.examinerName || ''}
                            onChange={(e) => handleInputChange('examinerName', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>
                  </details>
                </div>

                {/* What the application has already cited. Above the reference boxes
                    because it decides what belongs in them — a reference already of
                    record need not be cited again, and knowing that after typing the
                    list in is knowing it too late.

                    Hidden for now by request. The panel, its endpoint and the matching
                    against what is typed below are all untouched — set the flag above to
                    true to show it again. */}
                {SHOW_ART_ALREADY_CITED && (
                  <IdsOfRecordPanel
                    appNo={formData.appNo}
                    referencesText={formData.referencesText}
                    foreignReferencesText={formData.foreignReferencesText}
                    nplText={formData.nplText}
                    generatedIdsName={generatedIDS?.idsName || undefined}
                  />
                )}

                {/* Read the art instead of typing it.
                    Above the three boxes because it fills them: a filer who has the
                    documents should not be transcribing numbers off them, which is where
                    a digit gets transposed — and a transposed digit on an SB/08 cites a
                    document that does not exist. */}
                <div className="space-y-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold">Read from the documents</h3>
                      <p className="text-xs text-slate-600">
                        Upload the prior art as PDFs and the numbers and NPL citations are
                        read off them. You confirm everything before it reaches the form.
                      </p>
                    </div>
                    <div>
                      <input
                        id="ids-prior-art"
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={event => {
                          handlePriorArtUpload(event.target.files);
                          // Cleared so uploading the same file twice still fires a change.
                          event.target.value = '';
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={artReading}
                        onClick={() => document.getElementById('ids-prior-art')?.click()}
                      >
                        {artReading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        {artReading ? 'Reading…' : 'Upload prior art'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* US Patent References */}
                <div className="space-y-2">
                  <h3 className="text-base font-semibold">US Patent References</h3>
                  <div className="space-y-1">
                    <Label htmlFor="references" className="text-sm">US Patents/Publications (one per line)</Label>
                    <Textarea
                      id="references"
                      placeholder="Enter patent numbers, one per line (e.g., US10123456, US2020/0123456)"
                      rows={5}
                      value={formData.referencesText}
                      onChange={(e) => handleInputChange('referencesText', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Foreign Patent References */}
                <div className="space-y-2">
                  <h3 className="text-base font-semibold">Foreign Patent References</h3>
                  <div className="space-y-1">
                    <Label htmlFor="foreignReferences" className="text-sm">Foreign Patents (one per line)</Label>
                    <Textarea
                      id="foreignReferences"
                      placeholder="Enter foreign patent numbers, one per line (e.g., EP3456789, WO2020123456, JP2019123456)"
                      rows={3}
                      value={formData.foreignReferencesText}
                      onChange={(e) => handleInputChange('foreignReferencesText', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* NPL References */}
                <div className="space-y-2">
                  <h3 className="text-base font-semibold">Non-Patent Literature (NPL)</h3>
                  <div className="space-y-1">
                    <Label htmlFor="npl" className="text-sm">NPL References (one per line)</Label>
                    <Textarea
                      id="npl"
                      placeholder="Enter NPL references, one per line"
                      rows={3}
                      value={formData.nplText}
                      onChange={(e) => handleInputChange('nplText', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Signature Selection */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold">Signature</h3>
                    <a
                      href="https://www.uspto.gov/sites/default/files/documents/sigexamples_alt_text.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      title="USPTO Signature Examples"
                    >
                      Examples <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="signature" className="text-sm">Select Signature</Label>
                    {settings.signatures && settings.signatures.length > 0 ? (
                      <Select
                        value={formData.signatureId || settings.defaultSignatureId || ''}
                        onValueChange={(value) => handleInputChange('signatureId', value)}
                      >
                        <SelectTrigger id="signature">
                          <SelectValue placeholder="Select a signature..." />
                        </SelectTrigger>
                        <SelectContent>
                          {settings.signatures.map((sig) => (
                            <SelectItem key={sig.id} value={sig.id}>
                              {sig.name} #{sig.registrationNumber}
                              {sig.label && ` - ${sig.label}`}
                              {settings.defaultSignatureId === sig.id && ' - Default'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded border">
                        No signatures saved. Go to Settings to add a signature.
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData({
                        appNo: '',
                        docketNo: '',
                        idsName: '',
                        filingDate: '',
                        firstInventor: '',
                        artUnit: '',
                        examinerName: '',
                        referencesText: '',
                        foreignReferencesText: '',
                        nplText: '',
                      });
                    }}
                  >
                    Clear
                  </Button>
                  <Button onClick={handleSubmit} disabled={isSubmitting} size="sm">
                    {isSubmitting && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
                    Generate IDS
                  </Button>
                </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>IDS History</CardTitle>
                    <CardDescription>View and download your previously generated IDS forms</CardDescription>
                  </div>
                  {history.length > 0 && (
                    <div className="text-sm text-gray-600">
                      {searchQuery ? (
                        <span>
                          Showing {filteredHistory.length} of {history.length} forms
                        </span>
                      ) : (
                        <span>{history.length} total forms</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search history (name, app number, patent numbers, inventors, etc.)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No IDS forms generated yet</p>
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No IDS forms match your search</p>
                    <p className="text-sm mt-2">Try a different search term</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredHistory.map((item) => {
                      const isExpanded = expandedItems.has(item.idsId);
                      return (
                        <div
                          key={item.idsId}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                            <div className="flex-1">
                              <div className="font-medium">{item.idsName}</div>
                              <div className="text-sm text-gray-600">
                                App: {item.appNo} {item.docketNo && `• Docket: ${item.docketNo}`}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(item.timestamp).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleExpanded(item.idsId)}
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                {isExpanded ? 'Hide' : 'View'} Details
                              </Button>
                              {item.fileKey && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(item.fileKey!, `${item.idsName}.pdf`)}
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Download
                                </Button>
                              )}
                              {((item.usPatents && item.usPatents.length > 0) ||
                                (item.usApplications && item.usApplications.length > 0) ||
                                (item.foreignPatents && item.foreignPatents.length > 0)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadReferencePdfs(item)}
                                  disabled={downloadingPdfs[item.idsId]}
                                >
                                  {downloadingPdfs[item.idsId] ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{downloadPdfStatus[item.idsId] || 'Downloading...'}</>
                                  ) : (
                                    <><Download className="w-4 h-4 mr-2" />Ref PDFs</>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="border-t bg-gray-50 p-4 space-y-4">
                              {/* Application Information */}
                              {item.applicationInfo && (
                                <div>
                                  <h4 className="font-semibold text-sm mb-2">Application Information</h4>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {item.applicationInfo.filingDate && (
                                      <div><span className="text-gray-600">Filing Date:</span> {item.applicationInfo.filingDate}</div>
                                    )}
                                    {item.applicationInfo.firstInventor && (
                                      <div><span className="text-gray-600">First Inventor:</span> {item.applicationInfo.firstInventor}</div>
                                    )}
                                    {item.applicationInfo.artUnit && (
                                      <div><span className="text-gray-600">Art Unit:</span> {item.applicationInfo.artUnit}</div>
                                    )}
                                    {item.applicationInfo.examinerName && (
                                      <div><span className="text-gray-600">Examiner:</span> {item.applicationInfo.examinerName}</div>
                                    )}
                                    {item.applicationInfo.attorneyDocket && (
                                      <div><span className="text-gray-600">Attorney Docket:</span> {item.applicationInfo.attorneyDocket}</div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* US Patents */}
                              {item.usPatents && item.usPatents.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm mb-2">US Patents ({item.usPatents.length})</h4>
                                  <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                    {item.usPatents.map((patent: any, idx: number) => (
                                      <div key={idx} className="pl-2 border-l-2 border-blue-200">
                                        <span className="font-medium">{patent.patent_number}</span>
                                        {patent.patentee && <span className="text-gray-600"> - {patent.patentee}</span>}
                                        {patent.issue_date && <span className="text-gray-500"> ({patent.issue_date})</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* US Applications/Publications */}
                              {item.usApplications && item.usApplications.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm mb-2">US Publications ({item.usApplications.length})</h4>
                                  <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                    {item.usApplications.map((pub: any, idx: number) => (
                                      <div key={idx} className="pl-2 border-l-2 border-green-200">
                                        <span className="font-medium">{pub.publication_number}</span>
                                        {pub.applicant && <span className="text-gray-600"> - {pub.applicant}</span>}
                                        {pub.publication_date && <span className="text-gray-500"> ({pub.publication_date})</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Foreign Patents */}
                              {item.foreignPatents && item.foreignPatents.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm mb-2">Foreign Patents ({item.foreignPatents.length})</h4>
                                  <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                    {item.foreignPatents.map((foreign: any, idx: number) => (
                                      <div key={idx} className="pl-2 border-l-2 border-purple-200">
                                        <span className="font-medium">{foreign.country}{foreign.docNumber}{foreign.kind}</span>
                                        {foreign.patentee && <span className="text-gray-600"> - {foreign.patentee}</span>}
                                        {foreign.date && <span className="text-gray-500"> ({foreign.date})</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* NPL */}
                              {item.nonPatentLiterature && item.nonPatentLiterature.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm mb-2">Non-Patent Literature ({item.nonPatentLiterature.length})</h4>
                                  <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                    {item.nonPatentLiterature.map((npl: any, idx: number) => (
                                      <div key={idx} className="pl-2 border-l-2 border-orange-200 text-xs">
                                        {npl.citation}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>IDS Settings</CardTitle>
                <CardDescription>Configure your IDS generation preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {isLoadingSettings ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
                    <p className="text-sm text-gray-600">Loading settings...</p>
                  </div>
                ) : (
                  <>
                {/* Sorting Preferences */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold border-b pb-2">Reference Sorting</h3>

                  {/* US Patents Sorting */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-sm text-gray-700">US Patents</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="usPatentsSortField">Sort By</Label>
                        <Select
                          value={settings.usPatentsSortField}
                          onValueChange={(value) => handleSettingChange('usPatentsSortField', value as SortField)}
                        >
                          <SelectTrigger id="usPatentsSortField">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="input_order">Input Order</SelectItem>
                            <SelectItem value="document_number">Document Number</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="applicant_name">Applicant Name</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="usPatentsSortDirection">Direction</Label>
                        <Select
                          value={settings.usPatentsSortDirection}
                          onValueChange={(value) => handleSettingChange('usPatentsSortDirection', value as SortDirection)}
                        >
                          <SelectTrigger id="usPatentsSortDirection">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Ascending</SelectItem>
                            <SelectItem value="desc">Descending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* US Publications Sorting */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-sm text-gray-700">US Publications</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="usPublicationsSortField">Sort By</Label>
                        <Select
                          value={settings.usPublicationsSortField}
                          onValueChange={(value) => handleSettingChange('usPublicationsSortField', value as SortField)}
                        >
                          <SelectTrigger id="usPublicationsSortField">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="input_order">Input Order</SelectItem>
                            <SelectItem value="document_number">Document Number</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="applicant_name">Applicant Name</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="usPublicationsSortDirection">Direction</Label>
                        <Select
                          value={settings.usPublicationsSortDirection}
                          onValueChange={(value) => handleSettingChange('usPublicationsSortDirection', value as SortDirection)}
                        >
                          <SelectTrigger id="usPublicationsSortDirection">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Ascending</SelectItem>
                            <SelectItem value="desc">Descending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Foreign Patents Sorting */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-sm text-gray-700">Foreign Patents</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="foreignPatentsSortField">Sort By</Label>
                        <Select
                          value={settings.foreignPatentsSortField}
                          onValueChange={(value) => handleSettingChange('foreignPatentsSortField', value as SortField)}
                        >
                          <SelectTrigger id="foreignPatentsSortField">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="input_order">Input Order</SelectItem>
                            <SelectItem value="document_number">Document Number</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="applicant_name">Patentee Name</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="foreignPatentsSortDirection">Direction</Label>
                        <Select
                          value={settings.foreignPatentsSortDirection}
                          onValueChange={(value) => handleSettingChange('foreignPatentsSortDirection', value as SortDirection)}
                        >
                          <SelectTrigger id="foreignPatentsSortDirection">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Ascending</SelectItem>
                            <SelectItem value="desc">Descending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Docket Number Preference */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Docket Number Preference</h3>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="docketNumberPreference">When Application Info is Retrieved</Label>
                      <Select
                        value={settings.docketNumberPreference}
                        onValueChange={(value) => handleSettingChange('docketNumberPreference', value as 'input' | 'retrieved')}
                      >
                        <SelectTrigger id="docketNumberPreference">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="retrieved">Use Retrieved Docket Number (if available)</SelectItem>
                          <SelectItem value="input">Always Use Input Docket Number (if provided)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-2">
                        {settings.docketNumberPreference === 'retrieved'
                          ? 'Will use the docket number from USPTO data when available, falling back to your input if not found.'
                          : 'Will prioritize the docket number you enter in the form. If you leave it blank, will fall back to retrieved data.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Patentee Name Preference — one choice per kind of citation */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Patentee Name</h3>
                  <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                    <p className="text-xs text-gray-500">
                      Which name fills the &ldquo;Name of Patentee or Applicant&rdquo;
                      column. Whichever you choose, the other is used where the one you
                      asked for is not on record — a citation identified by the wrong name
                      is a correctable defect, one identified by neither cannot be looked
                      up at all. Saved with your settings and used on every IDS you
                      generate.
                    </p>
                    {NAME_PREFERENCE_KINDS.map(kind => {
                      const value =
                        settings.namePreferences?.[kind.key] ||
                        settings.patenteePreference ||
                        'inventor';
                      return (
                        <div key={kind.key} className="space-y-2">
                          <Label htmlFor={`namePreference-${kind.key}`}>{kind.label}</Label>
                          <Select
                            value={value}
                            onValueChange={(next) =>
                              handleSettingChange('namePreferences', {
                                // Written whole, and seeded from what is in force rather
                                // than from what is stored: a firm still on the old single
                                // setting would otherwise have the two kinds they did not
                                // touch silently revert to the default.
                                usPatents:
                                  settings.namePreferences?.usPatents ||
                                  settings.patenteePreference ||
                                  'inventor',
                                usPublications:
                                  settings.namePreferences?.usPublications ||
                                  settings.patenteePreference ||
                                  'inventor',
                                foreignPatents:
                                  settings.namePreferences?.foreignPatents ||
                                  settings.patenteePreference ||
                                  'inventor',
                                [kind.key]: next as NamePreference,
                              })
                            }
                          >
                            <SelectTrigger id={`namePreference-${kind.key}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inventor">
                                Inventor{kind.key === 'usPatents' ? ' (recommended)' : ''}
                              </SelectItem>
                              <SelectItem value="applicant">
                                Applicant or assignee, where available
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500">{kind.hint}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Signature Management */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 border-b pb-2">
                    <h3 className="text-lg font-semibold">Signatures</h3>
                    <a
                      href="https://www.uspto.gov/sites/default/files/documents/sigexamples_alt_text.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      title="USPTO Signature Examples"
                    >
                      Examples <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  {/* Add New Signature */}
                  <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <h4 className="font-medium text-sm text-gray-700">Add New Signature</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="sigName">Name *</Label>
                        <Input
                          id="sigName"
                          placeholder="e.g., John Doe"
                          value={newSignature.name}
                          onChange={(e) => setNewSignature(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sigRegNumber">Registration Number *</Label>
                        <Input
                          id="sigRegNumber"
                          placeholder="e.g., 12345"
                          value={newSignature.registrationNumber}
                          onChange={(e) => setNewSignature(prev => ({ ...prev, registrationNumber: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sigLabel">Label (Optional)</Label>
                        <Input
                          id="sigLabel"
                          placeholder="e.g., Primary Attorney"
                          value={newSignature.label}
                          onChange={(e) => setNewSignature(prev => ({ ...prev, label: e.target.value }))}
                        />
                      </div>
                    </div>
                    <Button onClick={handleAddSignature} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Signature
                    </Button>
                  </div>

                  {/* Saved Signatures List */}
                  {settings.signatures && settings.signatures.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-gray-700">Saved Signatures</h4>
                      <div className="space-y-2">
                        {settings.signatures.map((sig) => (
                          <div key={sig.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <PenTool className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">{sig.name}</span>
                                <span className="text-sm text-gray-600">#{sig.registrationNumber}</span>
                                {sig.label && (
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                    {sig.label}
                                  </span>
                                )}
                                {settings.defaultSignatureId === sig.id && (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {settings.defaultSignatureId !== sig.id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSetDefaultSignature(sig.id)}
                                >
                                  Set as Default
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveSignature(sig.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveSettings} disabled={isSavingSettings}>
                    {isSavingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
                </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Mounted at the page rather than beside the upload button: it stays open while
          the reading runs, and the filer can switch tabs without losing the result. */}
      <IdsPriorArtDialog
        open={artDialogOpen}
        onOpenChange={handleArtDialogChange}
        extracted={artExtracted}
        loading={artReading}
        error={artError}
        onConfirm={handlePriorArtConfirm}
      />
    </div>
  );
}
